import {
  type RefObject,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { FieldValue } from "./field";
import {
  buildApplication,
  type DemographicAnswer,
  type EmploymentAnswers,
} from "@utils/jben/build_application.ts";
import { isUploadedFile } from "@utils/uploads/uploaded_file.ts";
import type {
  JobPost,
  Question,
  ComplianceAnswers,
  SearchSpecificFields,
} from "../../types/jben/job_post";
import {
  validateApplication,
  type EducationErrors,
  type ErrorWithLink,
} from "@utils/jben/validate_application.ts";
import { isDemographicDataConsentRequired } from "@utils/jben/demographic_data_consent";
import { submitApplication } from "@utils/jben/submit_application";
import { buildQuickApplyProfile } from "@utils/jben/build_quick_apply_profile";
import { isSamePhoneNumber } from "@utils/jben/phone_helpers";
import { scrollToElement } from "@utils/jben/scroll_to_element";
import { type EducationAnswer } from "./education_section";
import { type PhoneError } from "./phone_field";
import { type Location } from "@components/ui/location_input";
import { appendSearchParams } from "@utils/jben/append_search_params";
import { toSnakeCase } from "@utils/jben/string_helpers";
import UploadManager from "../../utils/uploads/upload_manager";
import useWhoami, { type WhoamiProfile } from "@/queries/use_whoami";
import { Autofiller } from "@components/job_post/autofill/autofiller";
import { EducationAutofiller } from "@components/job_post/autofill/education_autofiller";
import { EmploymentAutofiller } from "@components/job_post/autofill/employment_autofiller";
import { useSafePostHog } from "@/hooks/use_posthog";
import { useShowFlash } from "@sprout/flash.tsx";
import { type Field as FieldType } from "../../types/jben/job_post";

export type FormField = FieldType & {
  label: string;
  description?: string | null;
  required: boolean;
  multiSelectStyle?: string;
  allowManual: boolean;
  error?: string;
  allowedFiletypes?: string[];
  custom: boolean;
};

function displayEducation(jobPost: JobPost) {
  if (!jobPost || !jobPost.education_config) return false;
  for (const value of Object.values(jobPost.education_config)) {
    if (value === "optional" || value === "required") return true;
  }
  return false;
}

// Derives the profile-backed answer keys by running buildQuickApplyProfile
// against a Proxy that records every answers key it reads, so this set can
// never drift from what the builder actually saves.
function quickApplyAnswerKeys(questions: Question[], jobPost: JobPost): Set<string> {
  const readKeys = new Set<string>();
  const recordingAnswers = new Proxy({} as Record<string, FieldValue>, {
    get(_target, property) {
      if (typeof property === "string") readKeys.add(property);
      return undefined;
    },
  });
  buildQuickApplyProfile(recordingAnswers, [], [], questions, jobPost, {} as WhoamiProfile);
  return readKeys;
}

function classifyFileAnswers(answers: Record<string, FieldValue>) {
  const now = Date.now();
  const expired: string[] = [];
  const upcoming: Array<{ key: string; msUntilExpiry: number }> = [];
  for (const [key, value] of Object.entries(answers)) {
    if (!isUploadedFile(value)) continue;
    const msUntilExpiry = value.expiresAt - now;
    if (msUntilExpiry <= 0) expired.push(key);
    else upcoming.push({ key, msUntilExpiry });
  }
  return { expired, upcoming };
}

interface UseApplicationFormParams {
  jobPost: JobPost & SearchSpecificFields;
  questions: Question[];
  submitPath: string;
  onSuccess?: (confirmationPath: string) => void;
  showBasicFields?: boolean;
  educationApiUrl?: string;
  dreamJobAvailable?: boolean;
  isRecommendation?: boolean;
  dirtyRef: RefObject<() => boolean>;
  fromOnboarding?: boolean;
}

export function useApplicationForm({
  jobPost,
  questions,
  submitPath,
  onSuccess,
  showBasicFields = true,
  educationApiUrl,
  dreamJobAvailable = false,
  isRecommendation = false,
  dirtyRef,
  fromOnboarding = false,
}: UseApplicationFormParams) {
  const { t } = useTranslation("job_post");

  const [answers, setAnswers] = useState<Record<string, FieldValue>>({});
  const [phoneError, setPhoneError] = useState<PhoneError | null>(null);
  const [demographicAnswers, setDemographicAnswers] = useState<Record<string, DemographicAnswer[]>>(
    {},
  );
  const [complianceAnswers, setComplianceAnswers] = useState<ComplianceAnswers>({});
  const [employmentAnswers, setEmploymentAnswers] = useState<EmploymentAnswers>([{ key: 0 }]);
  const [educationAnswers, setEducationAnswers] = useState<EducationAnswer[]>([{ key: 0 }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [demographicErrors, setDemographicErrors] = useState<Record<string, string>>({});
  const [complianceErrors, setComplianceErrors] = useState<Record<string, string | ErrorWithLink>>(
    {},
  );
  const [employmentErrors, setEmploymentErrors] = useState<Record<string, string>[]>([]);
  const [educationErrors, setEducationErrors] = useState<Record<number, EducationErrors>>({});
  const [eeocErrors, setEeocErrors] = useState<Record<string, string>>({});
  const [submitErrorMessage, setSubmitErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [prefillComplete, setPrefillComplete] = useState(false);
  const [markAsDreamJob, setMarkAsDreamJob] = useState(false);
  const [saveToQuickApply, setSaveToQuickApply] = useState(false);
  const [quickApplyProfileModified, setQuickApplyProfileModified] = useState(false);

  const s3Manager = useRef(new UploadManager());
  const hasPrefilled = useRef(false);
  const baseline = useRef<string | null>(null);

  const serialize = useCallback(
    () =>
      JSON.stringify(
        { answers, demographicAnswers, complianceAnswers, employmentAnswers, educationAnswers },
        (key, value) => {
          if (key === "key") return undefined;
          if (key === "phone" && typeof value === "string") return value.replace(/[^0-9+]/g, "");
          if (isUploadedFile(value)) return { name: value.name, url: value.url };
          return value;
        },
      ),
    [answers, demographicAnswers, complianceAnswers, employmentAnswers, educationAnswers],
  );

  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;

  useEffect(() => {
    if (prefillComplete) baseline.current = serializeRef.current();
  }, [prefillComplete, jobPost.fingerprint]);

  dirtyRef.current = () => baseline.current !== null && serialize() !== baseline.current;

  const quickApplyKeys = useMemo(
    () => quickApplyAnswerKeys(questions, jobPost),
    [questions, jobPost],
  );

  const { data: whoamiProfile, isStale: whoamiIsStale, isError: whoamiIsError } = useWhoami();
  const queryClient = useQueryClient();
  const { currentUserEmail } = usePage<{ currentUserEmail?: string }>().props;

  const showQuickApplyBanner = !!whoamiProfile && quickApplyProfileModified;

  const displayEducationForm = useMemo(() => displayEducation(jobPost), [jobPost]);

  const demographicDataConsentRequired = useMemo(() => {
    return isDemographicDataConsentRequired(jobPost, Object.values(demographicAnswers));
  }, [demographicAnswers, jobPost]);

  const posthog = useSafePostHog();

  useEffect(() => {
    s3Manager.current.initUploaders(s3Manager.current.inputFilesFrom(questions));
  }, [questions]);

  useEffect(() => {
    const expiredMessage = t("file_upload.expired") || "";
    const { expired, upcoming } = classifyFileAnswers(answers);
    const expiredSet = new Set(expired);

    const handles = upcoming.map(({ key, msUntilExpiry }) =>
      setTimeout(() => {
        setErrors((current) => ({ ...current, [key]: expiredMessage }));
      }, msUntilExpiry),
    );

    setErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of expired) {
        if (next[key] !== expiredMessage) {
          next[key] = expiredMessage;
          changed = true;
        }
      }
      for (const key of Object.keys(next)) {
        if (next[key] !== expiredMessage || expiredSet.has(key)) continue;
        delete next[key];
        changed = true;
      }
      return changed ? next : prev;
    });

    return () => handles.forEach((h) => clearTimeout(h));
  }, [answers, t]);

  useEffect(() => {
    const { data_compliance, demographic_questions } = jobPost;
    if (!data_compliance) return;

    const initialComplianceAnswers: ComplianceAnswers = {};

    if (data_compliance.single_purpose_consent) {
      if (data_compliance.requires_processing_consent) {
        initialComplianceAnswers["gdpr_processing_consent_given"] = false;
      }
      if (data_compliance.requires_retention_consent) {
        initialComplianceAnswers["gdpr_retention_consent_given"] = false;
      }
    } else if (data_compliance.retention_period) {
      initialComplianceAnswers["gdpr_consent_given"] = false;
    }

    if (
      data_compliance.single_purpose_consent &&
      data_compliance.demographic_data_consent_applies &&
      demographic_questions
    ) {
      initialComplianceAnswers["gdpr_demographic_data_consent_given"] = false;
    }

    setComplianceAnswers(initialComplianceAnswers);
  }, [jobPost]);

  useEffect(() => {
    const elementWithError = document.querySelector<HTMLElement>(
      '[aria-invalid="true"], [data-error="true"], .helper-text--error',
    );
    if (!elementWithError) return;
    scrollToElement(elementWithError);
    elementWithError.focus({ preventScroll: true });
  }, [errors, demographicErrors, complianceErrors, employmentErrors, educationErrors, eeocErrors]);

  useEffect(() => {
    if (!whoamiProfile || hasPrefilled.current) return;
    if (whoamiIsStale && !whoamiIsError) return;
    // We can't prefill education questions without the API URL to fetch the org-specific options
    if (displayEducationForm && !educationApiUrl) return;
    hasPrefilled.current = true;

    const eeocQuestions = jobPost.eeoc_sections?.flatMap((s) => s.questions) ?? [];

    const autofiller = new Autofiller(
      answers,
      setAnswers,
      questions,
      eeocQuestions,
      new EducationAutofiller(
        educationAnswers,
        setEducationAnswers,
        jobPost.education_config,
        educationApiUrl,
      ),
      new EmploymentAutofiller(employmentAnswers, setEmploymentAnswers, jobPost.employment),
    );

    void autofiller.fill(whoamiProfile).then(() => setPrefillComplete(true));
  }, [
    whoamiProfile,
    whoamiIsStale,
    whoamiIsError,
    questions,
    jobPost,
    displayEducationForm,
    educationApiUrl,
  ]);

  const showFlash = useShowFlash();

  const handleSubmitErrorMessage = useCallback(
    (message: string, error?: Error) => {
      if (error) console.error(error);
      setSubmitErrorMessage(message);
      showFlash(message, "error");
      setSubmitting(false);
    },
    [showFlash],
  );

  const fields = useMemo(
    () =>
      questions.map((question): FormField => {
        const field = question.fields[0];
        const isFile = field.type === "input_file";
        const allowManual = isFile && question.fields.length > 1;

        return {
          ...field,
          label: question.label,
          description: question.description,
          required: question.required,
          multiSelectStyle: question.multi_select_style,
          allowManual,
          error: errors[field.name],
          allowedFiletypes: field.allowed_filetypes,
          custom: field.name.startsWith("question_"),
        };
      }),
    [questions, errors],
  );

  const trackingParams = new URLSearchParams(window.location.search);

  const prepareRequest = (event: SyntheticEvent) => {
    event.preventDefault();
    if (submitting) return;

    setSubmitting(true);

    const application = buildApplication(
      jobPost,
      { ...answers, email: currentUserEmail ?? "" },
      demographicAnswers,
      complianceAnswers,
      employmentAnswers,
      educationAnswers,
      trackingParams,
    );

    const validationResult = validateApplication(jobPost, application, phoneError, showBasicFields);
    const { expired } = classifyFileAnswers(answers);
    const expiredMessage = t("file_upload.expired") || "";
    const expiredFileErrors = Object.fromEntries(expired.map((key) => [key, expiredMessage]));

    if (!validationResult.isValid || expired.length > 0) {
      setSubmitting(false);
      setErrors({ ...validationResult.basicErrors, ...expiredFileErrors });
      setDemographicErrors(validationResult.demographicErrors);
      setComplianceErrors(validationResult.complianceErrors);
      setEmploymentErrors(validationResult.employmentErrors);
      setEducationErrors(validationResult.educationErrors);
      setEeocErrors(validationResult.eeocErrors);
      return;
    }

    return application;
  };

  const handleErrors = async (responseData: unknown) => {
    const body = responseData as { code: string; message: string };

    if (body.code === "invalid-attributes") {
      const attributes = body.message.split(",");
      const eeocFields =
        jobPost.eeoc_sections?.flatMap(
          (section) =>
            section?.questions?.map((question) => ({
              name: question.fields[0].name,
              label: question.label,
            })) || [],
        ) ?? [];
      const complianceFields = [
        {
          name: jobPost.data_compliance?.single_purpose_consent
            ? "gdpr_processing_consent_given"
            : "gdpr_consent_given",
          label: "",
        },
      ];
      const allFields = [...fields, ...eeocFields, ...complianceFields];
      const invalidErrors: { [key: string]: string } = {};

      for (const attribute of attributes) {
        const field = allFields.find((f) => f.name === attribute);
        if (!field) continue;
        if (attribute.includes("gdpr")) {
          invalidErrors[attribute] = t("application.accept_terms");
          continue;
        }
        invalidErrors[attribute] = t("application.errors.invalid_attribute", {
          attribute: field.label.toLowerCase(),
        });
      }

      setErrors(invalidErrors);
      setEeocErrors(invalidErrors);
      setComplianceErrors(invalidErrors);

      if (Object.keys(invalidErrors).length === 0) {
        handleSubmitErrorMessage(t("application.errors.generic_failure"));
      }
    } else if (body.code === "invalid-attachment") {
      const invalidErrors: { [key: string]: string } = {};
      const fieldNames = fields.map((field) => field.name);
      const errorMessages = body.message.split(".").filter((message: string) => !!message);

      errorMessages.forEach((message: string) => {
        const match = message.trim().match(/^Uploaded (.*?) has an unsupported file type$/);
        if (match && match[1]) {
          const erroredFieldName = toSnakeCase(match[1]);
          if (fieldNames.includes(erroredFieldName)) {
            invalidErrors[erroredFieldName] = t("application.errors.invalid_filetype");
          }
        }
      });
      setErrors(invalidErrors);

      const invalidErrorsLength = Object.keys(invalidErrors).length;
      if (invalidErrorsLength === 0 || invalidErrorsLength !== errorMessages.length) {
        handleSubmitErrorMessage(t("application.errors.generic_invalid_filetype"));
      }
    } else if (
      body.code === "not-live" ||
      body.code === "board-not-live" ||
      body.code === "not-found"
    ) {
      handleSubmitErrorMessage(t("application.errors.job_no_longer_live"));
    } else if (body.code === "duplicate-submission") {
      handleSubmitErrorMessage(t("application.errors.duplicate_submission"));
    } else {
      handleSubmitErrorMessage(t("application.errors.generic_failure"));
    }

    setSubmitting(false);
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    const application = prepareRequest(event);
    if (!application) return;

    const shouldSaveToQuickApply = fromOnboarding || saveToQuickApply;
    const quickApply =
      shouldSaveToQuickApply && whoamiProfile
        ? buildQuickApplyProfile(
            answers,
            employmentAnswers,
            educationAnswers,
            questions,
            jobPost,
            whoamiProfile,
          )
        : null;

    try {
      const response = await submitApplication({
        application,
        submitPath,
        fingerprint: jobPost.fingerprint,
        markAsDreamJob,
        quickApply,
      });

      if (response.status >= 200 && response.status < 300) {
        if (quickApply) {
          void queryClient.invalidateQueries({ queryKey: ["whoami"] });
        }
        posthog?.capture("application_submitted", {
          apply_as_dream_job: markAsDreamJob,
          dream_job_available: dreamJobAvailable,
          is_recommendation: isRecommendation,
          save_to_quick_apply: shouldSaveToQuickApply,
          quick_apply_banner_shown: showQuickApplyBanner,
          from_onboarding: fromOnboarding,
        });
        const confirmationPath = response.data?.goto || "/";
        if (onSuccess) {
          onSuccess(appendSearchParams(confirmationPath, trackingParams));
        } else {
          window.location.assign(appendSearchParams(confirmationPath, trackingParams));
        }
      } else {
        await handleErrors(response.data);
      }
    } catch (e) {
      const err = e as { response?: { data: unknown } };
      if (err.response) {
        await handleErrors(err.response.data);
      } else {
        handleSubmitErrorMessage(
          t("application.errors.generic_failure"),
          e instanceof Error ? e : undefined,
        );
      }
    }
  };

  const isUpdatingQuickApplyProfile = (key: string, value: FieldValue) => {
    if (!quickApplyKeys.has(key)) return false;
    if (key === "phone") return !isSamePhoneNumber(answers.phone, value);
    return true;
  };

  const handleChange = (key: string, value: FieldValue) => {
    if (isUpdatingQuickApplyProfile(key, value)) setQuickApplyProfileModified(true);
    setAnswers({ ...answers, [key]: value });
  };

  const handleFileInputChange = (key: string, value: FieldValue) => {
    if (key === "resume" && isUploadedFile(value)) setQuickApplyProfileModified(true);
    const textKey = `${key}_text`;
    let updatedAnswers: { [key: string]: string | FieldValue | null };

    if (typeof value === "string") {
      updatedAnswers = { [key]: null, [textKey]: value };
    } else {
      updatedAnswers = { [key]: value, [textKey]: null };
    }

    setAnswers((previousAnswers) => ({ ...previousAnswers, ...updatedAnswers }));
  };

  const handleCandidateLocationChange = (location: Location | null) => {
    if (location) setQuickApplyProfileModified(true);
    if (!location) {
      return setAnswers({
        ...answers,
        location: null,
        latitude: null,
        longitude: null,
        country_short_name: null,
      });
    }

    setAnswers({
      ...answers,
      location: location.display_name,
      latitude: location.latitude.toString(),
      longitude: location.longitude.toString(),
      country_short_name: location.country_short_name ?? null,
    });
  };

  const candidateLocation: Location | null = useMemo(() => {
    if (!answers.location) return null;
    return {
      display_name: answers.location as string,
      latitude: parseFloat(answers.latitude as string) || 0,
      longitude: parseFloat(answers.longitude as string) || 0,
      country_short_name: (answers.country_short_name as string) || null,
    };
  }, [answers.location, answers.latitude, answers.longitude, answers.country_short_name]);

  const handleDemographicChange = (key: string, value: DemographicAnswer[]) => {
    setDemographicAnswers({ ...demographicAnswers, [key]: value });
  };

  const handleComplianceChange = (key: keyof ComplianceAnswers, value: boolean) => {
    setComplianceAnswers({ ...complianceAnswers, [key]: value });

    if (
      "gdpr_retention_consent_given" in complianceAnswers &&
      key === "gdpr_processing_consent_given" &&
      !value
    ) {
      setComplianceAnswers((prev) => ({
        ...prev,
        gdpr_retention_consent_given: false,
      }));
    }
  };

  const hasCustomFields = fields.some((f) => f.custom);

  return {
    showBasicFields,
    fields,
    hasCustomFields,
    answers,
    errors,
    handleChange,
    handleFileInputChange,
    setPhoneError,
    candidateLocation,
    handleCandidateLocationChange,
    uploadManager: s3Manager.current,
    prefillComplete,
    currentUserEmail,
    employmentAnswers,
    setEmploymentAnswers,
    employmentErrors,
    educationAnswers,
    setEducationAnswers,
    educationErrors,
    displayEducationForm,
    demographicErrors,
    handleDemographicChange,
    eeocErrors,
    complianceAnswers,
    complianceErrors,
    handleComplianceChange,
    demographicDataConsentRequired,
    setQuickApplyProfileModified,
    submitting,
    submitErrorMessage,
    handleSubmit,
    markAsDreamJob,
    setMarkAsDreamJob,
    saveToQuickApply,
    setSaveToQuickApply,
    showQuickApplyBanner,
  };
}

export type ApplicationFormState = ReturnType<typeof useApplicationForm>;
